/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import Glean from '@mozilla/glean/web';
import GleanMetricsAPI from '@mozilla/glean/metrics';
import UAParser from 'ua-parser-js';
import { Entries } from 'type-fest';
import {
  GleanMetricsConfig,
  eventsMap,
  EventMapKeys,
  EventsMap,
  GleanPingMetrics,
  booleanEventPropertyNames,
  stringEventPropertyNames,
} from 'fxa-shared/metrics/glean/web/index';
import { accountsEvents } from 'fxa-shared/metrics/glean/web/pings';
import * as event from 'fxa-shared/metrics/glean/web/event';
import * as email from 'fxa-shared/metrics/glean/web/email';
import * as error from 'fxa-shared/metrics/glean/web/error';
import * as promoQrMobile from 'fxa-shared/metrics/glean/web/promoQrMobile';
import * as reg from 'fxa-shared/metrics/glean/web/reg';
import * as login from 'fxa-shared/metrics/glean/web/login';
import * as cachedLogin from 'fxa-shared/metrics/glean/web/cachedLogin';
import * as passwordReset from 'fxa-shared/metrics/glean/web/passwordReset';
import * as cad from 'fxa-shared/metrics/glean/web/cad';
import * as cadFirefox from 'fxa-shared/metrics/glean/web/cadFirefox';
import * as cadApproveDevice from 'fxa-shared/metrics/glean/web/cadApproveDevice';
import * as cadMobilePair from 'fxa-shared/metrics/glean/web/cadMobilePair';
import * as cadMobilePairUseApp from 'fxa-shared/metrics/glean/web/cadMobilePairUseApp';
import * as cadRedirectDesktop from 'fxa-shared/metrics/glean/web/cadRedirectDesktop';
import * as cadRedirectMobile from 'fxa-shared/metrics/glean/web/cadRedirectMobile';
import * as accountPref from 'fxa-shared/metrics/glean/web/accountPref';
import * as accountBanner from 'fxa-shared/metrics/glean/web/accountBanner';
import * as firefoxPromo from 'fxa-shared/metrics/glean/web/firefoxPromo';
import * as deleteAccount from 'fxa-shared/metrics/glean/web/deleteAccount';
import * as thirdPartyAuth from 'fxa-shared/metrics/glean/web/thirdPartyAuth';
import * as postVerifySetPassword from 'fxa-shared/metrics/glean/web/postVerifySetPassword';
import * as passkeyEnterPassword from 'fxa-shared/metrics/glean/web/passkeyEnterPassword';
import * as passkey from 'fxa-shared/metrics/glean/web/passkey';
import { userIdSha256, userId } from 'fxa-shared/metrics/glean/web/account';
import {
  appFramework,
  cmsCustomizationEnrollment,
} from 'fxa-shared/metrics/glean/web/event';
import {
  oauthClientId,
  service,
} from 'fxa-shared/metrics/glean/web/relyingParty';
import {
  deviceType,
  entrypoint,
  flowId,
} from 'fxa-shared/metrics/glean/web/session';
import * as utm from 'fxa-shared/metrics/glean/web/utm';
import * as entrypointQuery from 'fxa-shared/metrics/glean/web/entrypoint';
import { Integration } from '../../models';
import { MetricsFlow } from '../metrics-flow';
import { currentAccount } from '../../lib/cache';

type DeviceTypes = 'mobile' | 'tablet' | 'desktop';
export type GleanMetricsContext = {
  metricsFlow: MetricsFlow | null;
  userAgent: string;
  integration: Integration;
};
type PingFn = ReturnType<typeof createEventFn>;
type SubmitPingFn = () => Promise<void>;
type GleanMetricsT = {
  initialize: (
    config: GleanMetricsConfig,
    context: GleanMetricsContext
  ) => void;
  setEnabled: (enabled: boolean) => void;
  getEnabled: () => boolean;
  isDone: () => Promise<void>;
  pageLoad: (url?: string) => void;
  handleClickEvent(event: Event): void;

  useGlean: () => { enabled: boolean };
} & {
  [k in EventMapKeys]: { [eventKey in keyof EventsMap[k]]: PingFn };
};

let EXEC_MUTEX = false;
const lambdas: SubmitPingFn[] = [];

const submitPing = async (fn: SubmitPingFn) => {
  lambdas.push(fn);

  if (EXEC_MUTEX) return;

  EXEC_MUTEX = true;
  let f: SubmitPingFn | undefined;

  while ((f = lambdas.shift())) {
    await f();
  }

  EXEC_MUTEX = false;
};

let gleanEnabled = false;
let metricsContext: GleanMetricsContext;
let ua: UAParser | null;

const encoder = new TextEncoder();
const hashUid = async (uid: string) => {
  const data = encoder.encode(uid);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const uint8View = new Uint8Array(hash);
  const hex = uint8View.reduce(
    (str, byte) => str + ('00' + byte.toString(16)).slice(-2),
    ''
  );
  return hex;
};

const getDeviceType: () => DeviceTypes | void = () => {
  if (ua) {
    // This logic is from the old content server frontend
    const parsedType = ua.getDevice().type;
    switch (parsedType) {
      case 'mobile':
      case 'tablet':
        return parsedType;
      case 'smarttv':
      case 'wearable':
      case 'embedded':
        return 'mobile';
      default:
        return 'desktop';
    }
  }

  if (metricsContext?.userAgent) {
    ua = new UAParser(metricsContext.userAgent);
    return getDeviceType();
  }
};

const initMetrics = async () => {
  userId.set('');
  userIdSha256.set('');
  const account = currentAccount();
  try {
    if (account?.uid) {
      userId.set(account.uid);
      userIdSha256.set(await hashUid(account.uid));
    }
  } catch (e) {
    // noop
  }

  oauthClientId.set(metricsContext.integration.data.clientId || '');
  service.set(metricsContext.integration.data.service || '');

  deviceType.set(getDeviceType() || '');
  entrypoint.set(metricsContext.integration.data.entrypoint || '');
  flowId.set(metricsContext.metricsFlow?.flowId || '');

  utm.campaign.set(metricsContext.integration.data.utmCampaign || '');
  utm.content.set(metricsContext.integration.data.utmContent || '');
  utm.medium.set(metricsContext.integration.data.utmMedium || '');
  utm.source.set(metricsContext.integration.data.utmSource || '');
  utm.term.set(metricsContext.integration.data.utmTerm || '');

  entrypointQuery.variation.set(
    metricsContext.integration.data.entrypointVariation || ''
  );
  entrypointQuery.experiment.set(
    metricsContext.integration.data.entrypointExperiment || ''
  );

  appFramework.set('react');

  // If the user has any cms info, they are considered enrolled in CMS customization.
  const cmsInfo = !!metricsContext.integration?.getCmsInfo();
  cmsCustomizationEnrollment.set(cmsInfo);
};

const populateMetrics = async (gleanPingMetrics: GleanPingMetrics) => {
  await initMetrics();

  if (gleanPingMetrics?.event) {
    // The event here is the Glean `event` metric type, not an "metrics event" in
    // a more general sense

    for (const name of booleanEventPropertyNames) {
      const eventValue = gleanPingMetrics.event[name];
      if (eventValue) {
        event[name].set(eventValue);
      }
    }
    for (const name of stringEventPropertyNames) {
      event[name].set(gleanPingMetrics.event[name] || '');
    }
  }
};

const recordEventMetric = (
  eventName: string,
  gleanPingMetrics: GleanPingMetrics
) => {
  switch (eventName) {
    case 'email_first_view':
      email.firstView.record();
      break;
    case 'email_first_engage':
      email.firstEngage.record();
      break;
    case 'email_first_submit_success':
      email.firstSubmitSuccess.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'email_first_submit_fail':
      email.firstSubmitFail.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'email_first_google_oauth_start':
      email.firstGoogleOauthStart.record();
      break;
    case 'email_first_apple_oauth_start':
      email.firstAppleOauthStart.record();
      break;
    case 'email_first_passkey_submit':
      email.firstPasskeySubmit.record();
      break;
    case 'email_first_passkey_submit_frontend_error':
      email.firstPasskeySubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'email_first_passkey_submit_success':
      email.firstPasskeySubmitSuccess.record();
      break;
    case 'reg_view':
      reg.view.record();
      break;
    case 'reg_engage':
      reg.engage.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_submit':
      reg.submit.record();
      break;
    case 'reg_submit_success':
      reg.submitSuccess.record();
      break;
    case 'reg_signup_code_view':
      reg.signupCodeView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_signup_code_submit':
      reg.signupCodeSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_signup_code_submit_frontend_error':
      reg.signupCodeSubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_success_view':
      reg.successView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_age_invalid':
      reg.ageInvalid.record();
      break;
    case 'reg_change_email_link_click':
      reg.changeEmailLinkClick.record();
      break;
    case 'reg_why_do_we_ask_link_click':
      reg.whyDoWeAskLinkClick.record();
      break;
    case 'login_view':
      const loginViewExtra =
        typeof gleanPingMetrics.event?.thirdPartyLinks === 'boolean'
          ? {
              third_party_links: gleanPingMetrics.event.thirdPartyLinks,
            }
          : undefined;
      login.view.record(loginViewExtra);
      break;
    case 'login_forgot_pwd_submit':
      login.forgotPwdSubmit.record();
      break;
    case 'login_submit':
      login.submit.record();
      break;
    case 'login_submit_success':
      login.submitSuccess.record();
      break;
    case 'login_submit_frontend_error':
      login.submitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'login_diff_account_link_click':
      login.diffAccountLinkClick.record();
      break;
    case 'login_backup_code_view':
      login.backupCodeView.record();
      break;
    case 'login_backup_code_submit':
      login.backupCodeSubmit.record();
      break;
    case 'login_backup_code_success_view':
      login.backupCodeSuccessView.record();
      break;
    case 'login_backup_choice_view':
      login.backupChoiceView.record();
      break;
    case 'login_backup_choice_submit':
      login.backupChoiceSubmit.record();
      break;
    case 'login_engage':
      login.engage.record();
      break;
    case 'login_locked_account_banner_view':
      login.lockedAccountBannerView.record();
      break;
    case 'login_alternative_auth_view':
      login.alternativeAuthView.record();
      break;
    case 'login_alternative_auth_google_start':
      login.alternativeAuthGoogleStart.record();
      break;
    case 'login_alternative_auth_apple_start':
      login.alternativeAuthAppleStart.record();
      break;
    case 'login_passkey_submit':
      login.passkeySubmit.record();
      break;
    case 'login_passkey_submit_frontend_error':
      login.passkeySubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'login_passkey_submit_success':
      login.passkeySubmitSuccess.record();
      break;
    case 'login_alternative_auth_passkey_submit':
      login.alternativeAuthPasskeySubmit.record();
      break;
    case 'login_alternative_auth_passkey_submit_frontend_error':
      login.alternativeAuthPasskeySubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'login_alternative_auth_passkey_submit_success':
      login.alternativeAuthPasskeySubmitSuccess.record();
      break;
    case 'login_otp_passkey_submit':
      login.otpPasskeySubmit.record();
      break;
    case 'login_otp_passkey_submit_frontend_error':
      login.otpPasskeySubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'login_otp_passkey_submit_success':
      login.otpPasskeySubmitSuccess.record();
      break;
    case 'cached_login_forgot_pwd_submit':
      cachedLogin.forgotPwdSubmit.record();
      break;
    case 'cached_login_view':
      const cachedLoginViewExtra =
        typeof gleanPingMetrics.event?.thirdPartyLinks === 'boolean'
          ? {
              third_party_links: gleanPingMetrics.event.thirdPartyLinks,
            }
          : undefined;
      cachedLogin.view.record(cachedLoginViewExtra);
      break;
    case 'cached_login_submit':
      cachedLogin.submit.record();
      break;
    case 'cached_login_success_view':
      cachedLogin.successView.record();
      break;
    case 'login_email_confirmation_view':
      login.emailConfirmationView.record();
      break;
    case 'login_email_confirmation_submit':
      login.emailConfirmationSubmit.record();
      break;
    case 'login_email_confirmation_success_view':
      login.emailConfirmationSuccessView.record();
      break;
    case 'login_recovery_phone_success_view':
      login.recoveryPhoneSuccessView.record();
      break;
    case 'login_totp_form_view':
      login.totpFormView.record();
      break;
    case 'login_totp_code_submit':
      login.totpCodeSubmit.record();
      break;
    case 'login_totp_code_success_view':
      login.totpCodeSuccessView.record();
      break;
    case 'password_reset_create_new_recovery_key_message_click':
      passwordReset.createNewRecoveryKeyMessageClick.record();
      break;
    case 'password_reset_create_new_submit':
      passwordReset.createNewSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'password_reset_create_new_success_view':
      passwordReset.createNewSuccessView.record();
      break;
    case 'password_reset_create_new_view':
      passwordReset.createNewView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'password_reset_email_confirmation_different_account':
      passwordReset.emailConfirmationDifferentAccount.record();
      break;
    case 'password_reset_email_confirmation_signin':
      passwordReset.emailConfirmationSignin.record();
      break;
    case 'password_reset_email_confirmation_submit':
      passwordReset.emailConfirmationSubmit.record();
      break;
    case 'password_reset_email_confirmation_view':
      passwordReset.emailConfirmationView.record();
      break;
    case 'password_reset_email_confirmation_resend_code':
      passwordReset.emailConfirmationResendCode.record();
      break;
    case 'password_reset_recovery_key_cannot_find':
      passwordReset.recoveryKeyCannotFind.record();
      break;
    case 'password_reset_recovery_key_create_new_submit':
      passwordReset.recoveryKeyCreateNewSubmit.record();
      break;
    case 'password_reset_recovery_key_create_new_view':
      passwordReset.recoveryKeyCreateNewView.record();
      break;
    case 'password_reset_recovery_key_create_success_view':
      passwordReset.recoveryKeyCreateSuccessView.record();
      break;
    case 'password_reset_recovery_key_submit':
      passwordReset.recoveryKeySubmit.record();
      break;
    case 'password_reset_recovery_key_view':
      passwordReset.recoveryKeyView.record();
      break;
    case 'password_reset_submit':
      passwordReset.submit.record();
      break;
    case 'password_reset_view':
      passwordReset.view.record();
      break;
    case 'password_reset_backup_choice_view':
      passwordReset.backupChoiceView.record();
      break;
    case 'password_reset_backup_choice_submit':
      passwordReset.backupChoiceSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'third_party_auth_google_reg_start':
      thirdPartyAuth.googleRegStart.record();
      break;
    case 'third_party_auth_apple_reg_start':
      thirdPartyAuth.appleRegStart.record();
      break;
    case 'third_party_auth_google_login_start':
      thirdPartyAuth.googleLoginStart.record();
      break;
    case 'third_party_auth_apple_login_start':
      thirdPartyAuth.appleLoginStart.record();
      break;
    case 'cad_view':
      cad.view.record();
      break;
    case 'cad_submit':
      cad.submit.record();
      break;
    case 'cad_startbrowsing_submit':
      cad.startbrowsingSubmit.record();
      break;
    case 'cad_firefox_view':
      cadFirefox.view.record();
      break;
    case 'cad_firefox_choice_view':
      cadFirefox.choiceView.record();
      break;
    case 'cad_firefox_choice_engage':
      cadFirefox.choiceEngage.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'cad_firefox_choice_submit':
      cadFirefox.choiceSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'cad_firefox_choice_notnow_submit':
      cadFirefox.choiceNotnowSubmit.record();
      break;
    case 'cad_firefox_sync_device_submit':
      cadFirefox.syncDeviceSubmit.record();
      break;
    case 'cad_firefox_notnow_submit':
      cadFirefox.notnowSubmit.record();
      break;
    case 'cad_approve_device_view':
      cadApproveDevice.view.record();
      break;
    case 'cad_approve_device_submit':
      cadApproveDevice.submit.record();
      break;
    case 'cad_mobile_pair_view':
      cadMobilePair.view.record();
      break;
    case 'cad_mobile_pair_submit':
      cadMobilePair.submit.record();
      break;
    case 'cad_mobile_pair_use_app_view':
      cadMobilePairUseApp.view.record();
      break;
    case 'cad_redirect_desktop_view':
      cadRedirectDesktop.view.record();
      break;
    case 'cad_redirect_desktop_default_view':
      cadRedirectDesktop.defaultView.record();
      break;
    case 'cad_redirect_desktop_download':
      cadRedirectDesktop.download.record();
      break;
    case 'cad_redirect_mobile_view':
      cadRedirectMobile.view.record();
      break;
    case 'account_pref_view':
      accountPref.view.record();
      break;
    case 'account_pref_recovery_key_submit':
      accountPref.recoveryKeySubmit.record();
      break;
    case 'account_pref_two_step_auth_submit':
      accountPref.twoStepAuthSubmit.record();
      break;
    case 'account_pref_two_step_auth_scan_code_link':
      accountPref.twoStepAuthScanCodeLink.record();
      break;
    case 'account_pref_two_step_auth_qr_view':
      accountPref.twoStepAuthQrView.record();
      break;
    case 'account_pref_two_step_auth_qr_code_success':
      accountPref.twoStepAuthQrCodeSuccess.record();
      break;
    case 'account_pref_two_step_auth_manual_code_view':
      accountPref.twoStepAuthManualCodeView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_codes_view':
      accountPref.twoStepAuthCodesView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_enter_code_view':
      accountPref.twoStepAuthEnterCodeView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_backup_choice_view':
      accountPref.twoStepAuthBackupChoiceView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_backup_choice_submit':
      accountPref.twoStepAuthBackupChoiceSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
        choice: gleanPingMetrics?.event?.['choice'] || '',
      });
      break;
    case 'account_pref_change_password_submit':
      accountPref.changePasswordSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_google_unlink_submit':
      accountPref.googleUnlinkSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_google_unlink_submit_confirm':
      accountPref.googleUnlinkSubmitConfirm.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_apple_unlink_submit':
      accountPref.appleUnlinkSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_apple_unlink_submit_confirm':
      accountPref.appleUnlinkSubmitConfirm.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_device_signout':
      accountPref.deviceSignout.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_google_play_submit':
      accountPref.googlePlaySubmit.record();
      break;
    case 'account_pref_apple_submit':
      accountPref.appleSubmit.record();
      break;
    case 'account_pref_display_name_submit':
      accountPref.displayNameSubmit.record();
      break;
    case 'account_pref_secondary_email_submit':
      accountPref.secondaryEmailSubmit.record();
      break;
    case 'account_pref_help':
      accountPref.help.record();
      break;
    case 'account_pref_bento_view':
      accountPref.bentoView.record({
        nimbus_user_id: gleanPingMetrics?.event?.['nimbusUserId'] || '',
      });
      break;
    case 'account_pref_bento_firefox_desktop':
      accountPref.bentoFirefoxDesktop.record();
      break;
    case 'account_pref_bento_firefox_mobile':
      accountPref.bentoFirefoxMobile.record();
      break;
    case 'account_pref_bento_monitor':
      accountPref.bentoMonitor.record();
      break;
    case 'account_pref_bento_relay':
      accountPref.bentoRelay.record();
      break;
    case 'account_pref_bento_vpn':
      accountPref.bentoVpn.record();
      break;
    case 'account_pref_two_step_auth_enter_code_success_view':
      accountPref.twoStepAuthEnterCodeSuccessView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_phone_submit_view':
      accountPref.twoStepAuthPhoneSubmitView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_phone_verify_view':
      accountPref.twoStepAuthPhoneVerifyView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_two_step_auth_phone_remove_success_view':
      accountPref.twoStepAuthPhoneRemoveSuccessView.record();
      break;
    case 'account_pref_two_step_auth_disable_modal_view':
      accountPref.twoStepAuthDisableModalView.record();
      break;
    case 'account_pref_two_step_auth_disable_success_view':
      accountPref.twoStepAuthDisableSuccessView.record();
      break;
    case 'account_pref_two_step_auth_complete_view':
      accountPref.twoStepAuthCompleteView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'delete_account_settings_submit':
      deleteAccount.settingsSubmit.record();
      break;
    case 'delete_account_view':
      deleteAccount.view.record();
      break;
    case 'delete_account_engage':
      deleteAccount.engage.record();
      break;
    case 'delete_account_submit':
      deleteAccount.submit.record();
      break;
    case 'delete_account_password_view':
      deleteAccount.passwordView.record();
      break;
    case 'delete_account_password_submit':
      deleteAccount.passwordSubmit.record();
      break;
    case 'account_pref_promo_monitor_view':
      accountPref.promoMonitorView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_promo_monitor_submit':
      accountPref.promoMonitorSubmit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_promo_vpn_view':
      accountPref.promoVpnView.record();
      break;
    case 'account_pref_promo_vpn_submit':
      accountPref.promoVpnSubmit.record();
      break;
    case 'account_pref_mfa_guard_view':
      accountPref.mfaGuardView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_mfa_guard_submit_success':
      accountPref.mfaGuardSubmitSuccess.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_passkey_create_view':
      accountPref.passkeyCreateView.record();
      break;
    case 'account_pref_passkey_create_submit_frontend_error':
      accountPref.passkeyCreateSubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_passkey_create_success_view':
      accountPref.passkeyCreateSuccessView.record();
      break;
    case 'account_pref_passkey_delete_view':
      accountPref.passkeyDeleteView.record();
      break;
    case 'account_pref_passkey_delete_submit_frontend_error':
      accountPref.passkeyDeleteSubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'account_pref_passkey_delete_success_view':
      accountPref.passkeyDeleteSuccessView.record();
      break;
    case 'account_banner_create_recovery_key_view':
      accountBanner.createRecoveryKeyView.record();
      break;
    case 'account_banner_add_recovery_phone_view':
      accountBanner.addRecoveryPhoneView.record();
      break;
    case 'account_banner_reactivation_success_view':
      accountBanner.reactivationSuccessView.record();
      break;
    case 'firefox_promo_connect_mobile_view':
      firefoxPromo.connectMobileView.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'firefox_promo_connect_mobile_submit':
      firefoxPromo.connectMobileSubmit.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'firefox_promo_connect_mobile_dismiss':
      firefoxPromo.connectMobileDismiss.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'firefox_promo_switch_to_firefox_view':
      firefoxPromo.switchToFirefoxView.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'firefox_promo_switch_to_firefox_submit':
      firefoxPromo.switchToFirefoxSubmit.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'firefox_promo_switch_to_firefox_dismiss':
      firefoxPromo.switchToFirefoxDismiss.record({
        mobile_device_count:
          gleanPingMetrics?.event?.['mobile_device_count'] || '',
      });
      break;
    case 'reg_otp_view':
      reg.otpView.record();
      break;
    case 'reg_otp_engage':
      reg.otpEngage.record();
      break;
    case 'reg_otp_submit':
      reg.otpSubmit.record();
      break;
    case 'reg_otp_submit_success':
      reg.otpSubmitSuccess.record();
      break;
    case 'reg_otp_submit_frontend_error':
      reg.otpSubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'reg_otp_email_confirmation_resend_code':
      reg.otpEmailConfirmationResendCode.record();
      break;
    case 'reg_otp_change_email':
      reg.otpChangeEmail.record();
      break;
    case 'login_otp_view':
      login.otpView.record();
      break;
    case 'login_otp_engage':
      login.otpEngage.record();
      break;
    case 'login_otp_submit':
      login.otpSubmit.record();
      break;
    case 'login_otp_submit_success':
      login.otpSubmitSuccess.record();
      break;
    case 'login_otp_submit_frontend_error':
      login.otpSubmitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'login_otp_email_confirmation_resend_code':
      login.otpEmailConfirmationResendCode.record();
      break;
    case 'login_otp_change_email':
      login.otpChangeEmail.record();
      break;
    case 'error_view':
      error.view.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'post_verify_set_password_view':
      postVerifySetPassword.view.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'post_verify_set_password_engage':
      postVerifySetPassword.engage.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'post_verify_set_password_submit':
      postVerifySetPassword.submit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'post_verify_set_password_submit_frontend_error':
      postVerifySetPassword.submitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'post_verify_set_password_success':
      postVerifySetPassword.success.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_button_view':
      passkey.buttonView.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_auth_success':
      passkey.authSuccess.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_enter_password_view':
      passkeyEnterPassword.view.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_enter_password_engage':
      passkeyEnterPassword.engage.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_enter_password_submit':
      passkeyEnterPassword.submit.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_enter_password_submit_frontend_error':
      passkeyEnterPassword.submitFrontendError.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'passkey_enter_password_success':
      passkeyEnterPassword.success.record({
        reason: gleanPingMetrics?.event?.['reason'] || '',
      });
      break;
    case 'promo_qr_mobile_view':
      promoQrMobile.view.record();
      break;
  }
};

const createEventFn =
  (eventName: string) =>
  (gleanPingMetrics: GleanPingMetrics = {}) => {
    if (!gleanEnabled) {
      return;
    }

    const fn = async () => {
      event.name.set(eventName);
      await populateMetrics(gleanPingMetrics);

      // recording the event metric triggers the event ping because Glean is initialized with `maxEvents: 1`
      recordEventMetric(eventName, gleanPingMetrics);

      accountsEvents.submit();
    };

    submitPing(fn);
  };

export const GleanMetrics: Pick<
  GleanMetricsT,
  | 'initialize'
  | 'setEnabled'
  | 'getEnabled'
  | 'useGlean'
  | 'isDone'
  | 'pageLoad'
  | 'handleClickEvent'
> = {
  initialize: async (
    config: GleanMetricsConfig,
    context: GleanMetricsContext
  ) => {
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1859629
    // Starting with glean.js v2, accessing localStorage during
    // initialization could cause an error
    try {
      if (config.enabled) {
        Glean.initialize(config.applicationId, config.uploadEnabled, {
          appDisplayVersion: config.appDisplayVersion,
          channel: config.appChannel,
          serverEndpoint: config.serverEndpoint,
          enableAutoPageLoadEvents: true,
          enableAutoElementClickEvents: true,
        });
        Glean.setLogPings(config.logPings);
        if (config.debugViewTag) {
          Glean.setDebugViewTag(config.debugViewTag);
        }
      }
      GleanMetrics.setEnabled(config.enabled);
      metricsContext = context;
      ua = null;

      // try to initialize any available metrics prior to any automatic Glean
      // events
      await initMetrics();
    } catch (_) {
      // set some states so we won't try to do anything with glean.js later
      config.enabled = false;
      gleanEnabled = false;
    }
  },

  setEnabled: (enabled: boolean) => {
    gleanEnabled = enabled;
    Glean.setUploadEnabled(gleanEnabled);
  },

  getEnabled: () => {
    return gleanEnabled;
  },

  useGlean: () => ({ enabled: GleanMetrics.getEnabled() }),

  pageLoad: (url?: string) => {
    if (url) {
      GleanMetricsAPI.pageLoad({ url });
    } else {
      GleanMetricsAPI.pageLoad();
    }
  },

  handleClickEvent(event: Event) {
    GleanMetricsAPI.handleClickEvent(event);
  },

  /**
   * The ping calls are awaited internally for ease of use and that works in
   * most cases.  But in the scenario where we want to wait for the pings to
   * finish before we unload the page, we are doing so, crudely, here.  Do not
   * emit more pings after calling this function.
   */
  isDone: () =>
    new Promise((resolve) => {
      const checkForEmptyFnList = () => {
        if (lambdas.length === 0 && !EXEC_MUTEX) {
          setTimeout(resolve, 5);
        } else {
          setTimeout(checkForEmptyFnList, lambdas.length * 5 || 5);
        }
      };

      checkForEmptyFnList();
    }),
};

for (const [page, events] of Object.entries(eventsMap) as Entries<EventsMap>) {
  (GleanMetrics as any)[page] = Object.entries(events).reduce(
    (acc: { [key: string]: PingFn }, [evt, name]) => {
      acc[evt] = createEventFn(name);
      return acc;
    },
    {}
  );
}

export default GleanMetrics as GleanMetricsT;
